import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { listUsers, saveUser, deleteUser } from "../../../../services/platform_admin_service";

/**
 * usePlatformUsers — Hook paginado para listagem de usuários globais.
 */
const usePlatformUsers = () => {
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalItems: 0, totalPages: 0 });
    const [filters, setFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchUsers = useCallback(async (page = 1, pageSize = 25, currentFilters = {}) => {
        try {
            setLoading(true);
            setError(null);
            const result = await listUsers({ page, pageSize, filters: currentFilters });
            setItems(result.items || []);
            setPagination(result.pagination || {});
        } catch (err) {
            console.error("[usePlatformUsers]", err);
            setError(err.message || "Erro ao carregar usuários.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers(1, 25, filters);
    }, [fetchUsers, filters]);

    const goToPage = (page) => fetchUsers(page, pagination.pageSize, filters);
    const applyFilters = (newFilters) => setFilters(newFilters);

    const saveInternalUser = async (userData) => {
        try {
            setLoading(true);
            setError(null);
            const result = await saveUser(userData);
            await fetchUsers(pagination.page || 1, pagination.pageSize || 25, filters);
            toast.success(userData.uid ? "Colaborador atualizado com sucesso!" : "Colaborador criado com sucesso!");
            return result;
        } catch (err) {
            const msg = err.message || "Erro ao salvar colaborador.";
            setError(msg);
            toast.error(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const deactivateUser = async (uid) => {
        try {
            setLoading(true);
            setError(null);
            const result = await deleteUser(uid);
            await fetchUsers(pagination.page || 1, pagination.pageSize || 25, filters);
            toast.success("Colaborador desativado com sucesso.");
            return result;
        } catch (err) {
            const msg = err.message || "Erro ao desativar colaborador.";
            setError(msg);
            toast.error(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        items,
        pagination,
        loading,
        error,
        goToPage,
        applyFilters,
        filters,
        saveInternalUser,
        deactivateUser,
    };
};

export default usePlatformUsers;
